import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "react-hot-toast";
import AppSidebar, { MobileTopBar } from "@/components/AppSidebar";
import {
  Moon, CalendarDays, History, BarChart3, Settings, Check,
  PlayCircle, FileText, MessagesSquare, PlusCircle, BookOpen,
  Presentation, Download, Eye, EyeOff,
} from "lucide-react";
import { getChapterInfo, TOTAL_CHAPTERS } from "@/lib/grudem";

const YT_REGEX =
  /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

const ytEmbed = (url) => {
  const m = url?.match(YT_REGEX);
  return m ? `https://www.youtube.com/embed/${m[1]}?rel=0&modestbranding=1` : null;
};

// Lightweight rich-text renderer: ## headings, - bullets, **bold**, paragraphs
const renderInline = (str) =>
  str.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? <strong key={i} className="font-semibold text-slate-800">{p.slice(2, -2)}</strong> : p
  );

export function NotesRenderer({ text }) {
  const lines = text.split("\n");
  const blocks = [];
  let bullets = [];
  const flushBullets = () => {
    if (bullets.length) {
      blocks.push({ type: "ul", items: bullets });
      bullets = [];
    }
  };
  lines.forEach((line) => {
    const t = line.trim();
    if (!t) { flushBullets(); return; }
    if (t.startsWith("## ") || t.startsWith("# ")) {
      flushBullets();
      blocks.push({ type: "h", text: t.replace(/^#+\s/, "") });
    } else if (t.startsWith("- ") || t.startsWith("• ")) {
      bullets.push(t.slice(2));
    } else {
      flushBullets();
      blocks.push({ type: "p", text: t });
    }
  });
  flushBullets();

  return (
    <div className="space-y-2.5">
      {blocks.map((b, i) => {
        if (b.type === "h")
          return <h3 key={i} className="text-sm font-bold text-indigo-700 uppercase tracking-wide pt-2">{renderInline(b.text)}</h3>;
        if (b.type === "ul")
          return (
            <ul key={i} className="space-y-1.5 pl-1">
              {b.items.map((item, li) => (
                <li key={li} className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed">
                  <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
                  <span>{renderInline(item)}</span>
                </li>
              ))}
            </ul>
          );
        return <p key={i} className="text-sm text-slate-700 leading-relaxed">{renderInline(b.text)}</p>;
      })}
    </div>
  );
}

export const wednesdayNavItems = (navigate, user, activeKey) => [
  { icon: CalendarDays, label: "This Week", active: activeKey === "this-week", onClick: () => navigate("/wednesday") },
  { icon: History, label: "Past Weeks", active: activeKey === "archive", onClick: () => navigate("/wednesday/archive") },
  { icon: BarChart3, label: "Book Progress", active: activeKey === "progress", onClick: () => navigate("/wednesday/progress") },
  ...(user?.role === "teacher"
    ? [{ icon: Settings, label: "Manage Sessions", active: activeKey === "admin", onClick: () => navigate("/wednesday/admin") }]
    : []),
];

export default function WednesdayNights({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSlidesViewer, setShowSlidesViewer] = useState(false);
  const saveTimeout = useRef(null);

  const navItems = wednesdayNavItems(navigate, user, searchParams.get("session_id") ? null : "this-week");

  const loadSession = useCallback(async (sessionId) => {
    setLoading(true);
    setShowSlidesViewer(false);
    try {
      const [sessionRes, responsesRes] = await Promise.all([
        axios.get(`${API}/wednesday/${sessionId}`),
        axios.get(`${API}/wednesday/${sessionId}/responses`),
      ]);
      setSession(sessionRes.data);
      setResponses(responsesRes.data || {});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const sessionIdParam = searchParams.get("session_id");
    axios.get(`${API}/wednesday`).then((res) => {
      setSessions(res.data);
      if (sessionIdParam) loadSession(sessionIdParam);
      else if (res.data.length > 0) loadSession(res.data[0].session_id);
      else setLoading(false);
    }).catch(() => setLoading(false));
  }, [searchParams, loadSession]);

  const handleResponseChange = useCallback((key, value) => {
    setResponses((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), text: value } }));
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => {
      setSaving(true);
      axios.post(`${API}/wednesday/${session.session_id}/responses`, {
        responses: [{ key, text: value }],
      }).then(() => setSaving(false)).catch(() => setSaving(false));
    }, 800);
  }, [session]);

  const handleSubmit = async () => {
    if (!session) return;
    try {
      const toSave = Object.entries(responses)
        .filter(([k]) => k.startsWith("discussion") && !k.endsWith("_submitted"))
        .map(([k, v]) => ({ key: k, text: v?.text || "" }));
      if (toSave.length > 0) {
        await axios.post(`${API}/wednesday/${session.session_id}/responses`, { responses: toSave });
      }
      await axios.post(`${API}/wednesday/${session.session_id}/responses/submit`, { prefix: "discussion" });
      const refreshed = await axios.get(`${API}/wednesday/${session.session_id}/responses`);
      setResponses(refreshed.data);
      toast.success("Discussion answers submitted!");
    } catch {
      toast.error("Failed to submit");
    }
  };

  const chapterInfo = session?.chapter_number ? getChapterInfo(session.chapter_number) : null;
  const coveredCount = new Set(
    sessions.filter((s) => s.is_published && s.chapter_number).map((s) => s.chapter_number)
  ).size;
  const embedUrl = ytEmbed(session?.youtube_url);
  const discussionSubmitted = responses["discussion_submitted"]?.text === "true";

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AppSidebar user={user} active="wednesday" brandLabel="Wednesday Nights" navItems={navItems} />
        <MobileTopBar brandLabel="Wednesday Nights" />
        <div className="md:ml-[68px] xl:ml-[260px] pt-14 md:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AppSidebar user={user} active="wednesday" brandLabel="Wednesday Nights" navItems={navItems} />
        <MobileTopBar brandLabel="Wednesday Nights" />
        <div className="md:ml-[68px] xl:ml-[260px] pt-14 md:pt-0">
          <div className="max-w-xl mx-auto px-4 py-16 text-center">
            <Moon className="w-12 h-12 text-indigo-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-700 mb-2">No Session Published Yet</h2>
            <p className="text-slate-500 text-sm mb-6">
              Check back soon — our journey through Grudem's Systematic Theology starts here.
            </p>
            {user?.role === "teacher" && (
              <Button
                data-testid="create-session-btn"
                onClick={() => navigate("/wednesday/admin")}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Create First Session
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AppSidebar user={user} active="wednesday" brandLabel="Wednesday Nights" navItems={navItems} />
      <MobileTopBar brandLabel="Wednesday Nights" />
      <div className="md:ml-[68px] xl:ml-[260px] pt-14 md:pt-0">
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge data-testid="wednesday-week-badge" className="bg-indigo-100 text-indigo-700 border-indigo-200 text-xs">
                  Week {session.week_number}
                </Badge>
                {session.session_date && (
                  <span className="text-xs text-slate-400">{session.session_date}</span>
                )}
                {!session.is_published && (
                  <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-xs">Draft</Badge>
                )}
                {saving && <span className="text-xs text-slate-400 italic">Saving…</span>}
              </div>
              <h1 data-testid="wednesday-topic" className="text-2xl font-bold text-slate-900">{session.topic}</h1>
              {chapterInfo && (
                <p className="text-sm text-indigo-600 font-medium mt-0.5">
                  Chapter {chapterInfo.n}: {chapterInfo.title}
                  <span className="text-slate-400 font-normal"> · Part {chapterInfo.part}: {chapterInfo.partTitle}</span>
                </p>
              )}
            </div>
            {sessions.length > 1 && (
              <select
                data-testid="wednesday-session-selector"
                value={session.session_id}
                onChange={(e) => loadSession(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 text-slate-700 bg-white focus:outline-none focus:border-indigo-400"
              >
                {sessions.map((s) => (
                  <option key={s.session_id} value={s.session_id}>
                    Week {s.week_number} — {s.topic}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Book progress mini-bar */}
          <button
            data-testid="wednesday-progress-bar"
            onClick={() => navigate("/wednesday/progress")}
            className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-left hover:border-indigo-300 transition-colors"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-indigo-500" />
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Systematic Theology Progress</p>
              </div>
              <p className="text-xs font-semibold text-indigo-600">{coveredCount} of {TOTAL_CHAPTERS} chapters</p>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-500 rounded-full transition-all"
                style={{ width: `${Math.round((coveredCount / TOTAL_CHAPTERS) * 100)}%` }}
              />
            </div>
          </button>

          {/* Description */}
          {session.description && (
            <p className="text-sm text-slate-600 leading-relaxed">{session.description}</p>
          )}

          {/* Teaching video */}
          {embedUrl && (
            <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm" data-testid="wednesday-video-card">
              <div className="flex items-center gap-2 mb-3">
                <PlayCircle className="w-4 h-4 text-indigo-500" />
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Teaching Video</p>
              </div>
              <div className="relative w-full rounded-xl overflow-hidden bg-slate-900" style={{ paddingBottom: "56.25%" }}>
                <iframe
                  src={embedUrl}
                  title="Teaching video"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}

          {/* Notes */}
          {session.notes && (
            <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm" data-testid="wednesday-notes-card">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-indigo-500" />
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Teaching Notes</p>
              </div>
              <NotesRenderer text={session.notes} />
            </div>
          )}

          {/* Slides */}
          {session.slides_file_id && (
            <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm" data-testid="wednesday-slides-card">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Presentation className="w-4 h-4 text-indigo-500" />
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Slides</p>
                  <span className="text-sm text-slate-600">{session.slides_filename || "Slides.pdf"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="slides-view-toggle-btn"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSlidesViewer((v) => !v)}
                    className="rounded-lg text-xs"
                  >
                    {showSlidesViewer ? (
                      <><EyeOff className="w-3.5 h-3.5 mr-1.5" /> Hide Viewer</>
                    ) : (
                      <><Eye className="w-3.5 h-3.5 mr-1.5" /> View in Browser</>
                    )}
                  </Button>
                  <a href={`${API}/files/${session.slides_file_id}`} target="_blank" rel="noreferrer" data-testid="slides-download-link">
                    <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs">
                      <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                    </Button>
                  </a>
                </div>
              </div>
              {showSlidesViewer && (
                <div className="mt-4 w-full h-[70vh] rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                  <iframe
                    src={`${API}/files/${session.slides_file_id}?disposition=inline`}
                    title="Slides"
                    className="w-full h-full"
                    data-testid="slides-iframe-viewer"
                  />
                </div>
              )}
            </div>
          )}

          {/* Discussion questions */}
          {session.discussion_questions?.length > 0 && (
            <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm" data-testid="wednesday-discussion-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MessagesSquare className="w-4 h-4 text-indigo-500" />
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Discussion Questions</p>
                </div>
                {discussionSubmitted && (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                    <Check className="w-3 h-3 mr-1" /> Submitted
                  </Badge>
                )}
              </div>

              <div className="space-y-4">
                {session.discussion_questions.map((q, qi) => {
                  const key = `discussion_q${qi}`;
                  const val = responses[key]?.text || "";
                  const sub = responses[key]?.submitted || discussionSubmitted;
                  return (
                    <div key={qi} className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">
                        <span className="font-bold text-indigo-600 mr-1">{qi + 1}.</span> {q}
                      </p>
                      {sub ? (
                        <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-3 py-2.5 text-sm text-slate-700">
                          {val || <span className="italic text-slate-400">No response</span>}
                        </div>
                      ) : (
                        <Textarea
                          data-testid={`discussion-q-${qi}`}
                          value={val}
                          onChange={(e) => handleResponseChange(key, e.target.value)}
                          placeholder="Write your answer here…"
                          rows={3}
                          className="text-sm resize-none rounded-xl border-slate-200 focus:border-indigo-400"
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {!discussionSubmitted ? (
                <Button
                  data-testid="submit-discussion-btn"
                  onClick={handleSubmit}
                  className="mt-5 w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Submit Answers
                </Button>
              ) : (
                <div className="mt-3 flex items-center gap-2 text-emerald-600 text-sm font-medium">
                  <Check className="w-4 h-4" /> Answers submitted
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
